import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { delay, interval, take, tap } from 'rxjs';

@Component({
	selector: 'menu',
	templateUrl: 'menu.component.html',
	styleUrls: ['./menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class MenuComponent {
	private static readonly dialogSelector = '[role="dialog"], [role="alertdialog"], [aria-modal="true"]';
	@ViewChild('menuTrigger') public menuTrigger: ElementRef<HTMLElement>;
	@ViewChild(MatMenuTrigger) public contextMenu: MatMenuTrigger;
	@Input() public panelClass: string;

	public contextMenuPosition = {
		x: 0, y: 0
	};

	constructor (private cdr: ChangeDetectorRef) {}

	public openMenu (event: MouseEvent, parentContainer: HTMLElement) {
		event.preventDefault();
		const triggerElement = this.getTriggerElement(event.target as HTMLElement);
		const existingDialogs = this.getOpenDialogs(triggerElement.ownerDocument);
		this.markLastMenuTrigger(triggerElement);
		const rect = parentContainer.getBoundingClientRect();
		const left = event.clientX - rect.left;
		const top = event.clientY - rect.top;

		if (left > 0 && top > 0) {
			this.contextMenuPosition.x = left;
			this.contextMenuPosition.y = top;
		} else {
			const buttonRect = (event.target as HTMLElement).getBoundingClientRect();
			this.contextMenuPosition.x = buttonRect.left - rect.left;
			this.contextMenuPosition.y = buttonRect.top - rect.top;
		}

		// Refocus to event target when menu closed
		this.contextMenu.menuClosed.asObservable().pipe(take(1), delay(100)).subscribe(() => {
			this.restoreTriggerFocus(triggerElement, existingDialogs);
		});

		// avoid glitchy menu open when right clicking again
		// alternatives tried -
		// opening menu in matMenuTrigger menuClosed observer method
		// stopping event propagation
		// changing the contextMenuPosition and just leaving it open
		interval(100)
			.pipe(
				take(1),
				tap(() => {
					// Since we hide this component
					// We need to move the trigger element into the parentContainer
					// So Mat Menu will able to locate the location of the element
					// Reason: hidden element position x/y will be 0
					parentContainer.appendChild(this.menuTrigger.nativeElement);
					this.cdr.detectChanges();
				}),
				delay(1), // open menu on next cycle to make sure menuTrigger rendered in the parentContainer
				tap(() => this.contextMenu.openMenu()),
				delay(1) // After menu opened, focus to the first element
			).subscribe(() => {
				const firstMenuItem = this.getMenuItems()[0];
				if (firstMenuItem != null) {
					firstMenuItem.focus();
				}
			});
	}

	public handleMenuKeyDown (event: KeyboardEvent): void {
		if (event.key === 'Tab' || event.key === 'Escape') {
			this.closeMenu();
			return;
		}

		if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		const menuItems = this.getMenuItems();
		if (menuItems.length === 0) {
			return;
		}
		const currentIndex = menuItems.findIndex((item) => item === document.activeElement);
		const direction = event.key === 'ArrowDown' ? 1 : -1;
		const nextIndex = currentIndex === -1
			? direction === 1 ? 0 : menuItems.length - 1
			: (currentIndex + direction + menuItems.length) % menuItems.length;

		menuItems[nextIndex].focus();
	}

	public closeMenu () {
		this.contextMenu.closeMenu();
	}

	private getTriggerElement (eventTarget: HTMLElement): HTMLElement {
		return eventTarget.closest('button, [tabindex], a, input, textarea, select') as HTMLElement ?? eventTarget;
	}

	private markLastMenuTrigger (triggerElement: HTMLElement): void {
		this.clearLastMenuTrigger(triggerElement.ownerDocument);
		triggerElement.setAttribute('data-last-menu-trigger', 'true');
	}

	private clearLastMenuTrigger (doc: Document): void {
		doc.querySelectorAll('[data-last-menu-trigger="true"]').forEach((element) => {
			element.removeAttribute('data-last-menu-trigger');
		});
	}

	private restoreTriggerFocus (triggerElement: HTMLElement, existingDialogs: HTMLElement[]): void {
		if (this.shouldPreserveDialogFocus(triggerElement, existingDialogs)) {
			return;
		}

		triggerElement.focus();
	}

	private shouldPreserveDialogFocus (triggerElement: HTMLElement, existingDialogs: HTMLElement[]): boolean {
		const document = triggerElement.ownerDocument;
		const activeDialog = document.activeElement?.closest(MenuComponent.dialogSelector);
		if (activeDialog != null && !activeDialog.contains(triggerElement)) {
			return true;
		}

		return this.getOpenDialogs(document).some((dialog) =>
			!existingDialogs.includes(dialog) && !dialog.contains(triggerElement)
		);
	}

	private getOpenDialogs (document: Document): HTMLElement[] {
		return [...document.querySelectorAll(MenuComponent.dialogSelector)]
			.filter((element): element is HTMLElement =>
				element instanceof HTMLElement &&
				window.getComputedStyle(element).display !== 'none' &&
				window.getComputedStyle(element).visibility !== 'hidden'
			);
	}

	private getMenuItems (): HTMLElement[] {
		return [...document.querySelectorAll(`#${this.contextMenu.menu.panelId} .mat-mdc-menu-item`)] as HTMLElement[];
	}
}
